import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { Link } from 'react-router-dom';
import { useCustomDialog } from '../../context/DialogContext';

const useIsMobile = () => {
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    useEffect(() => {
        const handler = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener('resize', handler);
        return () => window.removeEventListener('resize', handler);
    }, []);
    return isMobile;
};

const CouponModal = ({ coupon, token, onClose, onSaved }) => {
    const isEdit = !!coupon;
    const [form, setForm] = useState({
        coupon_code: coupon?.coupon_code || '',
        discount_type: coupon?.discount_type || 'percentage',
        discount_value: coupon?.discount_value || '',
        min_order_value: coupon?.min_order_value || '',
        expiry_date: coupon?.expiry_date || ''
    });
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    const handleField = (e) => {
        const { name, value } = e.target;
        setForm(f => ({ ...f, [name]: value }));
    };

    const handleSave = async (e) => {
        e.preventDefault();
        setSaving(true);
        setError('');
        try {
            const res = await fetch('https://menu.searchisend.in/apimenu/v2/v2_coupons.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({ 
                    action: isEdit ? 'update' : 'create', 
                    ...(isEdit ? { coupon_id: coupon.coupon_id } : {}),
                    ...form 
                })
            });
            const data = await res.json();
            if (data.success) { onSaved(); onClose(); }
            else setError(data.message || 'Failed to save.');
        } catch { setError('Network error.'); }
        finally { setSaving(false); }
    };

    const modalInp = { width: '100%', padding: '10px', border: '1px solid var(--border-color)', borderRadius: '5px', boxSizing: 'border-box', marginBottom: '12px', fontSize: '1rem' };
    const modalLb  = { display: 'block', fontWeight: '500', marginBottom: '4px', fontSize: '0.9em' };

    return ReactDOM.createPortal(
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
            <div style={{ backgroundColor: 'var(--card-bg-color)', borderRadius: 'var(--border-radius-md)', padding: 'clamp(18px, 4vw, 30px)', width: '100%', maxWidth: '500px', maxHeight: '90vh', overflow: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }} className="modal-content-animate">
                <h2 style={{ marginTop: 0 }}>{isEdit ? 'Edit Coupon' : 'Create New Coupon'}</h2>
                {error && <div style={{ padding: '12px', backgroundColor: '#f8d7da', color: '#721c24', borderRadius: '5px', marginBottom: '16px' }}>{error}</div>}
                <form onSubmit={handleSave}>
                    <label style={modalLb}>Coupon Code *</label>
                    <input name="coupon_code" required value={form.coupon_code} onChange={handleField} style={modalInp} placeholder="e.g. SAVE20" />

                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 200px), 1fr))', gap: '15px' }}>
                        <div>
                            <label style={modalLb}>Discount Type</label>
                            <select name="discount_type" value={form.discount_type} onChange={handleField} style={modalInp}>
                                <option value="percentage">Percentage (%)</option>
                                <option value="flat">Flat Amount (₹)</option>
                            </select>
                        </div>
                        <div>
                            <label style={modalLb}>Discount Value *</label>
                            <input type="number" step="0.01" name="discount_value" required value={form.discount_value} onChange={handleField} style={modalInp} />
                        </div>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 200px), 1fr))', gap: '15px' }}>
                        <div>
                            <label style={modalLb}>Min Order (₹)</label>
                            <input type="number" step="0.01" name="min_order_value" value={form.min_order_value} onChange={handleField} style={modalInp} />
                        </div>
                        <div>
                            <label style={modalLb}>Expiry Date</label>
                            <input type="date" name="expiry_date" value={form.expiry_date} onChange={handleField} style={modalInp} />
                        </div>
                    </div>

                    <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
                        <button type="submit" disabled={saving} style={{ flex: 1, padding: '12px', backgroundColor: 'var(--primary-orange)', color: '#fff', border: 'none', borderRadius: '50px', fontWeight: '600', cursor: 'pointer' }}>
                            {saving ? 'Saving...' : (isEdit ? 'Save Changes' : 'Create Coupon')}
                        </button>
                        <button type="button" onClick={onClose} style={{ flex: 1, padding: '12px', backgroundColor: 'var(--header-actions-bg)', color: 'var(--text-color)', border: '1px solid var(--border-color)', borderRadius: '50px', fontWeight: '600', cursor: 'pointer' }}>
                            Cancel
                        </button>
                    </div>
                </form>
            </div>
        </div>,
        document.body
    );
};

const ManageCoupons = ({ token, features }) => {
    const { alert: customAlert, confirm: customConfirm } = useCustomDialog();
    const [coupons, setCoupons] = useState([]);
    const [loading, setLoading] = useState(true);
    const [message, setMessage] = useState({ text: '', type: '' });
    const [modalCoupon, setModalCoupon] = useState(null); // {coupon} or true for new
    const [isCreate, setIsCreate] = useState(false);
    const isMobile = useIsMobile();

    const hasCoupons = features?.has_coupon_system;

    useEffect(() => {
        if (hasCoupons) {
            fetchCoupons();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hasCoupons]);

    if (features && !features.has_coupon_system) {
        return (
            <div className="admin-card" style={{ textAlign: 'center', padding: '60px 40px' }}>
                <Link to="/manage_v2_secure_auth/" className="sticky-back-btn">
    <i className="fas fa-arrow-left"></i> Back to Dashboard
</Link>
                <div style={{ width: '72px', height: '72px', background: 'linear-gradient(135deg, #fef3c7, #fde68a)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}>
                    <i className="fas fa-ticket-alt" style={{ fontSize: '1.8rem', color: '#f59e0b' }} />
                </div>
                <h2 style={{ margin: '0 0 10px 0' }}>Coupon System</h2>
                <p style={{ color: 'var(--text-color-secondary)', maxWidth: '400px', margin: '0 auto 28px', lineHeight: '1.65' }}>Create discount codes with expiry dates and usage limits. Share them via WhatsApp or flyers. Available on the Paid Plan.</p>
                <Link to="/manage_v2_secure_auth/subscription" style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '12px 28px', background: 'var(--primary-orange, #ea580c)', color: '#fff', borderRadius: '50px', fontWeight: '700', textDecoration: 'none', boxShadow: '0 4px 14px rgba(234,88,12,0.3)' }}>
                    <i className="fas fa-crown" /> Upgrade to Unlock
                </Link>
            </div>
        );
    }

    const fetchCoupons = async () => {
        try {
            const res = await fetch('https://menu.searchisend.in/apimenu/v2/v2_coupons.php?action=fetch', {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await res.json();
            if (data.success) {
                setCoupons(data.coupons);
            }
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const handleDuplicate = async (couponId) => {
        try {
            const res = await fetch('https://menu.searchisend.in/apimenu/v2/v2_coupons.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({ action: 'duplicate', coupon_id: couponId })
            });
            const data = await res.json();
            if (data.success) {
                setMessage({ text: `Duplicated as ${data.new_code}`, type: 'success' });
                fetchCoupons();
            } else {
                setMessage({ text: data.message, type: 'error' });
            }
        } catch (err) {
            setMessage({ text: 'Error duplicating coupon.', type: 'error' });
        } finally {
            setTimeout(() => setMessage({ text: '', type: '' }), 3000);
        }
    };

    const handleDelete = async (couponId) => {
        if (!await customConfirm('Delete this coupon?', 'Delete Coupon', 'danger')) return;
        try {
            const res = await fetch('https://menu.searchisend.in/apimenu/v2/v2_coupons.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({ action: 'delete', coupon_id: couponId })
            });
            const data = await res.json();
            if (data.success) fetchCoupons();
        } catch (err) { await customAlert('Error deleting coupon.', 'Error', 'danger'); }
    };

    const handleToggle = async (couponId, currentStatus) => {
        try {
            const res = await fetch('https://menu.searchisend.in/apimenu/v2/v2_coupons.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({ action: 'toggle', coupon_id: couponId, is_active: Number(currentStatus) === 1 ? 0 : 1 })
            });
            const data = await res.json();
            if (data.success) fetchCoupons();
        } catch (err) {}
    };

    return (
        <div className="admin-card">
            <Link to="/manage_v2_secure_auth/" className="sticky-back-btn">
                <i className="fas fa-arrow-left"></i> Back to Dashboard
            </Link>
            
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
                <h1 style={{margin: '0'}}>Manage Coupons</h1>
                <button 
                    onClick={() => { setModalCoupon(null); setIsCreate(true); }}
                    style={{ backgroundColor: 'var(--primary-orange)', color: 'white', border: 'none', padding: '10px 22px', borderRadius: '50px', fontWeight: '700', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' }}
                >
                    <i className="fas fa-plus" /> New Coupon
                </button>
            </div>

            {message.text && (
                <div style={{padding: '15px', borderRadius: '5px', marginBottom: '20px', fontWeight: '500', backgroundColor: message.type === 'success' ? '#d4edda' : '#f8d7da', color: message.type === 'success' ? '#155724' : '#721c24'}}>
                    {message.text}
                </div>
            )}

            <h2 style={{fontSize: '1.2rem', marginTop: '30px'}}>Your Coupons</h2>
            
            {loading ? (
                <div style={{padding: '20px', textAlign: 'center'}}>Loading...</div>
            ) : coupons.length === 0 ? (
                <div style={{padding: '20px', textAlign: 'center'}}>No coupons found.</div>
            ) : isMobile ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                    {coupons.map(coupon => (
                        <div key={coupon.coupon_id} style={{ backgroundColor: 'var(--card-bg-color)', padding: '15px', borderRadius: '12px', border: '1px solid var(--border-color)', boxShadow: '0 2px 5px rgba(0,0,0,0.05)' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                                <span style={{ fontWeight: '800', fontSize: '1.1em', color: 'var(--primary-orange)' }}>{coupon.coupon_code}</span>
                                <button onClick={() => handleToggle(coupon.coupon_id, coupon.is_active)} style={{ border: 'none', background: 'none', cursor: 'pointer', fontWeight: '600', color: Number(coupon.is_active) === 1 ? 'var(--green-accent)' : '#9ca3af', display: 'flex', alignItems: 'center', gap: '5px' }}>
                                    <i className={`fas fa-toggle-${Number(coupon.is_active) === 1 ? 'on' : 'off'}`}></i> {Number(coupon.is_active) === 1 ? 'Active' : 'Inactive'}
                                </button>
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '15px', fontSize: '0.9em', color: 'var(--text-color-secondary)' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Type:</span> <span style={{ fontWeight: '600', color: 'var(--text-color)' }}>{coupon.discount_type}</span></div>
                                <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Value:</span> <span style={{ fontWeight: '600', color: 'var(--text-color)' }}>{coupon.discount_type === 'percentage' ? `${coupon.discount_value}%` : `₹${coupon.discount_value}`}</span></div>
                                <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Min. Order:</span> <span style={{ fontWeight: '600', color: 'var(--text-color)' }}>₹{coupon.min_order_value}</span></div>
                                <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Expiry:</span> <span style={{ fontWeight: '600', color: 'var(--text-color)' }}>{coupon.expiry_date ? new Date(coupon.expiry_date).toLocaleDateString() : 'No expiry'}</span></div>
                            </div>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px' }}>
                                <button onClick={() => { setModalCoupon(coupon); setIsCreate(false); }} style={{ padding: '8px', backgroundColor: '#e0f2fe', color: '#0369a1', border: 'none', borderRadius: '8px', fontWeight: '600', cursor: 'pointer' }} title="Edit"><i className="fas fa-edit"></i> Edit</button>
                                <button onClick={() => handleDuplicate(coupon.coupon_id)} style={{ padding: '8px', backgroundColor: '#f3e8ff', color: '#6b21a8', border: 'none', borderRadius: '8px', fontWeight: '600', cursor: 'pointer' }} title="Duplicate"><i className="fas fa-copy"></i> Dup</button>
                                <button onClick={() => handleDelete(coupon.coupon_id)} style={{ padding: '8px', backgroundColor: '#fee2e2', color: '#b91c1c', border: 'none', borderRadius: '8px', fontWeight: '600', cursor: 'pointer' }} title="Delete"><i className="fas fa-trash"></i> Del</button>
                            </div>
                        </div>
                    ))}
                </div>
            ) : (
                <div style={{overflowX: 'auto'}}>
                    <table style={{width: '100%', borderCollapse: 'collapse', backgroundColor: 'var(--card-bg-color)'}}>
                        <thead>
                            <tr style={{backgroundColor: 'var(--header-actions-bg)'}}>
                                <th style={{border: '1px solid var(--border-color)', padding: '12px', textAlign: 'left'}}>Code</th>
                                <th style={{border: '1px solid var(--border-color)', padding: '12px', textAlign: 'left'}}>Type</th>
                                <th style={{border: '1px solid var(--border-color)', padding: '12px', textAlign: 'left'}}>Value</th>
                                <th style={{border: '1px solid var(--border-color)', padding: '12px', textAlign: 'left'}}>Min. Order</th>
                                <th style={{border: '1px solid var(--border-color)', padding: '12px', textAlign: 'left'}}>Expiry</th>
                                <th style={{border: '1px solid var(--border-color)', padding: '12px', textAlign: 'left'}}>Status</th>
                                <th style={{border: '1px solid var(--border-color)', padding: '12px', textAlign: 'left'}}>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {coupons.map(coupon => (
                                <tr key={coupon.coupon_id}>
                                    <td style={{border: '1px solid var(--border-color)', padding: '12px', fontWeight: 'bold'}}>{coupon.coupon_code}</td>
                                    <td style={{border: '1px solid var(--border-color)', padding: '12px'}}>{coupon.discount_type}</td>
                                    <td style={{border: '1px solid var(--border-color)', padding: '12px'}}>{coupon.discount_type === 'percentage' ? `${coupon.discount_value}%` : `₹${coupon.discount_value}`}</td>
                                    <td style={{border: '1px solid var(--border-color)', padding: '12px'}}>₹{coupon.min_order_value}</td>
                                    <td style={{border: '1px solid var(--border-color)', padding: '12px'}}>{coupon.expiry_date ? new Date(coupon.expiry_date).toLocaleDateString() : 'No expiry'}</td>
                                    <td style={{border: '1px solid var(--border-color)', padding: '12px'}}>
                                        <button onClick={() => handleToggle(coupon.coupon_id, coupon.is_active)} style={{border: 'none', background: 'none', cursor: 'pointer', fontWeight: '600', color: Number(coupon.is_active) === 1 ? 'var(--green-accent)' : '#9ca3af', display: 'flex', alignItems: 'center', gap: '5px'}}>
                                            <i className={`fas fa-toggle-${Number(coupon.is_active) === 1 ? 'on' : 'off'}`}></i> {Number(coupon.is_active) === 1 ? 'Active' : 'Inactive'}
                                        </button>
                                    </td>
                                    <td style={{border: '1px solid var(--border-color)', padding: '12px'}}>
                                        <div style={{display: 'flex', gap: '15px'}}>
                                            <button onClick={() => { setModalCoupon(coupon); setIsCreate(false); }} style={{color: 'var(--primary-orange)', background: 'none', border: 'none', fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '5px'}} title="Edit">
                                                <i className="fas fa-edit"></i> Edit
                                            </button>
                                            <button onClick={() => handleDuplicate(coupon.coupon_id)} style={{color: '#4338ca', background: 'none', border: 'none', fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '5px'}} title="Duplicate">
                                                <i className="fas fa-copy"></i> Duplicate
                                            </button>
                                            <button onClick={() => handleDelete(coupon.coupon_id)} style={{color: 'var(--red-accent)', background: 'none', border: 'none', fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '5px'}} title="Delete">
                                                <i className="fas fa-trash"></i> Delete
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {(isCreate || modalCoupon) && (
                <CouponModal
                    coupon={modalCoupon}
                    token={token}
                    onClose={() => { setModalCoupon(null); setIsCreate(false); }}
                    onSaved={() => { fetchCoupons(); }}
                />
            )}
        </div>
    );
};

export default ManageCoupons;
